"use client";

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  DEFAULT_ATTACK_DELAY_TICKS,
  LegacyGame,
  POSE_CELLS,
  getPoseCells,
  type GameSnapshot,
  type HeldInputs,
  type InputControl,
  type PieceId,
} from "../../lib/game";
import { useLegacyKeyboard } from "../hooks/useLegacyKeyboard";
import { recordLossAndMaybeShowInterstitial } from "../admob";
import type { KeyBindings } from "../hooks/useLegacyKeyboard";
import { useLegacyAudio, useLegacyAudioSettings } from "../hooks/useLegacyAudio";
import { BattleArena, type BoardView, type MatchStatus } from "./BattleArena";
import { BattleBoard } from "./BattleBoard";
import { CpuMode } from "./CpuMode";
import { AdSlot } from "./AdSlot";
import { NextPiece } from "./NextPiece";
import { type LegacyAction } from "./TouchControls";
import { normalizePlayerProfile, ROOM_NAME_MAX_LENGTH, ROOM_PROFILE_MAX_LENGTH, ROOM_TRIP_MAX_LENGTH } from "../profile";
import { create2chTrip, formatPlayerIdentity } from "../tripcode";
import {
  type ChatMessage,
  type GameRoomClientMessage,
  type GameRoomServerMessage,
  type JsonValue,
  type RoomMember,
  type RoomMatch,
  type RoomRecord,
  type RoomPhase,
  type RoomQuality,
  type RoomView,
  type TetrisView,
  RANK_TITLES,
  type RankTitle,
  isServerMessage,
} from "../room-protocol";

type RoomModeProps = {
  casualMode?: boolean;
  customMode?: boolean;
  initialRoom: string;
  initialPlayerName: string;
  initialPlayerId: string;
  keyBindings: KeyBindings;
  onBack: () => void;
  onOpenSettings?: () => void;
  settingsOpen?: boolean;
  backgroundIndex?: number;
  onCycleBackground?: () => void;
  onPlayerNameChange?: (name: string) => void;
  onPlayerIdChange?: (id: string) => void;
};

type ScreenPhase = "entry" | RoomPhase;

const EMPTY_INPUT: HeldInputs = {
  left: false,
  right: false,
  softDrop: false,
  hardDrop: false,
  rotateCW: false,
  rotateCCW: false,
};

const CASUAL_ROOM_CODE = "CASUAL01";
const CASUAL_WAIT_SECONDS = 60;
const CASUAL_CPU_NAMES = [
  "積み上げ侍",
  "落ちもの博士",
  "ブロック職人",
  "ワクワクAI",
  "四列狙い",
  "盤面の魔術師",
  "フロンティアCPU",
  "静かな猛者",
] as const;

const ACTION_TO_INPUT: Readonly<Record<LegacyAction, InputControl>> = {
  ccw: "rotateCCW",
  cw: "rotateCW",
  left: "left",
  right: "right",
  softDrop: "softDrop",
  hardDrop: "hardDrop",
};

function originForRoom(): string {
  const environment = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const configured = environment?.VITE_SIGNALING_ORIGIN?.trim();
  if (configured) return new URL(configured).origin;
  if (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)) return "http://127.0.0.1:8787";
  throw new Error("公開版のルームURLが設定されていません");
}

function roomWebSocketUrl(room: string): string {
  const origin = originForRoom();
  const url = new URL(origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `/v2/rooms/${encodeURIComponent(room)}/connect`;
  return url.toString();
}

function roomRejoinStorageKey(room: string): string {
  return `bwwk-room-rejoin-v1:${room}`;
}

function roomRejoinCookieName(room: string): string {
  return `bwwk_room_rejoin_${room}`;
}

function readRoomRejoinToken(room: string): string {
  try {
    const storage = window.localStorage;
    const token = storage?.getItem(roomRejoinStorageKey(room));
    if (token) return token;
  } catch {
    // Fall through to the cookie fallback below.
  }
  try {
    const name = `${roomRejoinCookieName(room)}=`;
    const entry = document.cookie.split("; ").find((cookie) => cookie.startsWith(name));
    return entry ? decodeURIComponent(entry.slice(name.length)) : "";
  } catch {
    return "";
  }
}

function writeRoomRejoinToken(room: string, token: string): void {
  try {
    const storage = window.localStorage;
    storage?.setItem(roomRejoinStorageKey(room), token);
  } catch {
    // Cookie fallback below keeps mobile/private webviews recoverable.
  }
  try {
    document.cookie = `${roomRejoinCookieName(room)}=${encodeURIComponent(token)}; Max-Age=3600; Path=/; SameSite=Lax; Secure`;
  } catch {
    // Rejoin is best effort when both storage mechanisms are unavailable.
  }
}

function removeRoomRejoinToken(room: string): void {
  try {
    window.localStorage?.removeItem(roomRejoinStorageKey(room));
  } catch {
    // Ignore unavailable storage.
  }
  try {
    document.cookie = `${roomRejoinCookieName(room)}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
  } catch {
    // Ignore unavailable cookies.
  }
}

function initialRoomValue(value: string, customMode: boolean, casualMode: boolean): string {
  if (casualMode) return CASUAL_ROOM_CODE;
  const normalized = value.trim().toUpperCase().replace(/[^A-Z0-9]/gu, "");
  if (customMode) {
    const hasRoomLink = typeof window !== "undefined" && new URL(window.location.href).searchParams.has("room");
    return hasRoomLink && normalized.length >= 6 ? normalized.slice(0, 12) : "";
  }
  return normalized.startsWith("ROOM") ? normalized : "ROOM1A";
}

const MULTI_ROOM_OPTIONS = Array.from({ length: 6 }, (_, index) => ({
  label: `ROOM${index + 1}`,
  value: `ROOM${index + 1}A`,
}));

const MULTI_ROOM_ENTRY_OPTIONS = [
  ...MULTI_ROOM_OPTIONS,
  { label: "精神と時の部屋", value: "ROOM7A" },
] as const;

const CHAT_COLOR_STORAGE_PREFIX = "bwwk-room-chat-color:";
const LEGACY_CHAT_COLOR_STORAGE_KEY = "bwwk-room-chat-color";
const DEFAULT_CHAT_COLOR = "#000000";

type RoomCount = {
  connectedCount: number;
  totalCount: number;
};

type RoomCountDialogPosition = {
  top: number;
  right: number;
};

const ROOM_COUNT_DIALOG_POSITION_KEY = "bwwk-room-count-dialog-position-v1";
const DEFAULT_ROOM_COUNT_DIALOG_POSITION: RoomCountDialogPosition = { top: 145, right: 6 };

function readRoomCountDialogPosition(): RoomCountDialogPosition {
  if (typeof window === "undefined") return DEFAULT_ROOM_COUNT_DIALOG_POSITION;
  try {
    const raw = window.localStorage.getItem(ROOM_COUNT_DIALOG_POSITION_KEY);
    if (!raw) return DEFAULT_ROOM_COUNT_DIALOG_POSITION;
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return DEFAULT_ROOM_COUNT_DIALOG_POSITION;
    const candidate = value as { top?: unknown; right?: unknown };
    if (typeof candidate.top !== "number" || !Number.isFinite(candidate.top) || typeof candidate.right !== "number" || !Number.isFinite(candidate.right)) {
      return DEFAULT_ROOM_COUNT_DIALOG_POSITION;
    }
    return { top: Math.round(candidate.top), right: Math.round(candidate.right) };
  } catch {
    return DEFAULT_ROOM_COUNT_DIALOG_POSITION;
  }
}

function normalizeChatColor(value: string): string {
  const normalized = value.trim().toUpperCase();
  return /^#[0-9A-F]{6}$/u.test(normalized) ? normalized : DEFAULT_CHAT_COLOR;
}

function chatColorStorageKey(profileId: string): string {
  const normalized = profileId.normalize("NFKC").trim().toLowerCase();
  return normalized ? `${CHAT_COLOR_STORAGE_PREFIX}${encodeURIComponent(normalized)}` : "";
}

function readChatColor(profileId: string): string {
  if (typeof window === "undefined") return DEFAULT_CHAT_COLOR;
  try {
    const storage = window.localStorage;
    const key = chatColorStorageKey(profileId);
    if (key) {
      const stored = storage.getItem(key);
      if (stored) return normalizeChatColor(stored);

      // Move the old device-wide value to the first profile that uses it.
      const legacy = storage.getItem(LEGACY_CHAT_COLOR_STORAGE_KEY);
      if (legacy) {
        const migrated = normalizeChatColor(legacy);
        storage.setItem(key, migrated);
        storage.removeItem(LEGACY_CHAT_COLOR_STORAGE_KEY);
        return migrated;
      }
    }
    return DEFAULT_CHAT_COLOR;
  } catch {
    return DEFAULT_CHAT_COLOR;
  }
}

function rgbFromHex(value: string): { r: number; g: number; b: number } {
  const hex = normalizeChatColor(value).slice(1);
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function hexFromRgb(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((channel) => Math.min(255, Math.max(0, Math.round(channel))).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

function randomRoomCpuName(): string {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return `${CASUAL_CPU_NAMES[bytes[0] % CASUAL_CPU_NAMES.length]}AI`;
}

function nextCells(piece: number): ReadonlyArray<readonly [number, number]> {
  if (piece < 1 || piece > 7) return [];
  return POSE_CELLS[piece as PieceId][0].map(({ dx, dy }) => [dx, dy] as const);
}

function randomCasualCpuName(): string {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return `${CASUAL_CPU_NAMES[bytes[0] % CASUAL_CPU_NAMES.length]}AI`;
}

function emptyView(): BoardView {
  return {
    active: [],
    board: Array.from({ length: 24 }, () => Array(10).fill(0)),
    nextCells: [],
    nextPiece: 0,
    pending: 0,
    received: 0,
    topOut: false,
    lines: 0,
    pieces: 0,
  };
}

function viewFromGame(game: LegacyGame, received: number): BoardView {
  const active = game.active;
  return {
    active: getPoseCells(active).map(({ x, y }) => ({ x, y, piece: active.piece })),
    blockIndex: game.blockIndex,
    board: game.board,
    nextCells: nextCells(game.nextPiece),
    nextPiece: game.nextPiece,
    pending: game.pendingGarbageLines,
    received,
    topOut: game.topOut,
  };
}

function viewFromWire(view: TetrisView): BoardView {
  return {
    blockIndex: view.pieces,
    active: [view.active].flatMap((active) =>
      getPoseCells({
        piece: active.piece as PieceId,
        rotation: active.rotation as 0 | 1 | 2 | 3,
        x: active.x,
        y: active.y,
      }).map(({ x, y }) => ({ x, y, piece: active.piece })),
    ),
    board: view.board,
    nextCells: nextCells(view.nextPiece),
    nextPiece: view.nextPiece,
    pending: view.pending,
    received: view.received,
    topOut: view.topOut,
    lines: view.lines,
    pieces: view.pieces ?? 0,
  };
}

function validCheckpoint(value: JsonValue | undefined): boolean {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function qualityLabel(quality: RoomQuality): string {
  return quality.samples ? `${Math.round(quality.rtt)}ms` : "測定中";
}

const EMPTY_RECORD: RoomRecord = {
  games: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  rank: "5級",
  rankGroup: "E",
  rankIndex: 0,
  experience: 0,
  winStreak: 0,
  lossStreak: 0,
  room6Locked: false,
};

type LiveMatchStats = {
  lines: number;
  pieces: number;
};

function normalizeRoomRecord(value: unknown): RoomRecord {
  const record = typeof value === "object" && value !== null ? value as Partial<RoomRecord> : {};
  const suppliedRank = typeof record.rank === "string" && (RANK_TITLES as readonly string[]).includes(record.rank)
    ? record.rank as RankTitle
    : undefined;
  const rankIndex = Number.isSafeInteger(record.rankIndex) && Number(record.rankIndex) >= 0 && Number(record.rankIndex) < RANK_TITLES.length
    ? Number(record.rankIndex)
    : suppliedRank === undefined ? 0 : RANK_TITLES.indexOf(suppliedRank);
  const rank = RANK_TITLES[rankIndex] ?? suppliedRank ?? "5級";
  const rankGroup = rankIndex >= 17 ? "S" : rankIndex >= 14 ? "A" : rankIndex >= 11 ? "B" : rankIndex >= 8 ? "C" : rankIndex >= 5 ? "D" : "E";
  return {
    games: Number.isSafeInteger(record.games) && Number(record.games) >= 0 ? Number(record.games) : 0,
    wins: Number.isSafeInteger(record.wins) && Number(record.wins) >= 0 ? Number(record.wins) : 0,
    losses: Number.isSafeInteger(record.losses) && Number(record.losses) >= 0 ? Number(record.losses) : 0,
    draws: Number.isSafeInteger(record.draws) && Number(record.draws) >= 0 ? Number(record.draws) : 0,
    rank,
    rankGroup,
    rankIndex,
    experience: Number.isSafeInteger(record.experience) && Number(record.experience) >= 0 ? Number(record.experience) : 0,
    winStreak: Number.isSafeInteger(record.winStreak) && Number(record.winStreak) >= 0 ? Number(record.winStreak) : 0,
    lossStreak: Number.isSafeInteger(record.lossStreak) && Number(record.lossStreak) >= 0 ? Number(record.lossStreak) : 0,
    room6Locked: record.room6Locked === true,
  };
}

function splitProfile(value: string): { name: string; trip: string } {
  const separator = value.indexOf("#");
  if (separator < 0) return { name: value.trim(), trip: "" };
  return {
    name: value.slice(0, separator).trim(),
    trip: value.slice(separator + 1).trim().replace(/^#+/u, ""),
  };
}

function matchStatusForMember(member: RoomMember | undefined, fallback?: RoomRecord): MatchStatus {
  const record = member?.record ?? fallback;
  const rank = member?.rank ?? record?.rank ?? "5級";
  const group = member?.rankGroup ?? record?.rankGroup ?? "E";
  if (!record) return { group, text: `${rank}  0勝 0敗` };
  const streak = record.winStreak !== 0
    ? ` 【 ${record.winStreak}連勝 】`
    : record.lossStreak !== 0
      ? ` 【 ${record.lossStreak}連敗 】`
      : "";
  return {
    group,
    text: `${rank}  ${record.wins}勝 ${record.losses}敗${streak}`,
  };
}

function liveMatchStatusForView(seat: 0 | 1, view: BoardView): MatchStatus {
  return {
    group: `P${seat + 1}`,
    text: `${view.lines ?? 0}LINES / ${view.pieces ?? 0}PIECES`,
  };
}

export function RoomMode({ casualMode = false, customMode = false, initialRoom, initialPlayerName, initialPlayerId, keyBindings, onBack, onOpenSettings, settingsOpen = false, backgroundIndex = 0, onCycleBackground, onPlayerNameChange, onPlayerIdChange }: RoomModeProps) {
  const [roomCode, setRoomCode] = useState(() => initialRoomValue(initialRoom, customMode, casualMode));
  const [playerName, setPlayerName] = useState(() => normalizePlayerProfile(initialPlayerName));
  const [playerId, setPlayerId] = useState(initialPlayerId);
  const [screen, setScreen] = useState<ScreenPhase>("entry");
  const [status, setStatus] = useState(casualMode ? "対戦相手を探しています…" : "IDと名前を入力してルームへ");
  const [error, setError] = useState(false);
  const [connected, setConnected] = useState(false);
  const [memberId, setMemberId] = useState("");
  const [room, setRoom] = useState<RoomView | null>(null);
  const [activeMatch, setActiveMatch] = useState<RoomMatch | null>(null);
  const [views, setViews] = useState<Record<string, BoardView>>({});
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [chatText, setChatText] = useState("");
  const [result, setResult] = useState<{ winners: string[]; reason: string } | null>(null);
  const [serverRecord, setServerRecord] = useState<RoomRecord>(EMPTY_RECORD);
  const [casualRemaining, setCasualRemaining] = useState(CASUAL_WAIT_SECONDS);
  const [casualFallback, setCasualFallback] = useState<string | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [chatColor, setChatColor] = useState(() => readChatColor(initialPlayerId));
  const [chatColorDraft, setChatColorDraft] = useState(() => readChatColor(initialPlayerId));
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [roomCountsOpen, setRoomCountsOpen] = useState(false);
  const [roomCounts, setRoomCounts] = useState<Record<string, RoomCount | null>>(() => Object.fromEntries(
    MULTI_ROOM_OPTIONS.map((option) => [option.value, { connectedCount: 0, totalCount: 0 }]),
  ));
  const [roomCountsLoading, setRoomCountsLoading] = useState(false);
  const [roomCountsError, setRoomCountsError] = useState("");
  const [roomCountDialogPosition, setRoomCountDialogPosition] = useState(readRoomCountDialogPosition);
  const [cpuEnabled, setCpuEnabled] = useState(false);
  const [roomCpuMatchId, setRoomCpuMatchId] = useState<string | null>(null);
  const [roomCpuOpponentName, setRoomCpuOpponentName] = useState("CPU");
  const [roomCpuHumanSeat, setRoomCpuHumanSeat] = useState<1 | 2>(1);

  const socketRef = useRef<WebSocket | null>(null);
  const memberIdRef = useRef("");
  const roomRef = useRef<RoomView | null>(null);
  const matchRef = useRef<RoomMatch | null>(null);
  const screenRef = useRef<ScreenPhase>("entry");
  const gamesRef = useRef(new Map<string, LegacyGame>());
  const inputSeqRef = useRef(0);
  const inputStatesRef = useRef(new Map<string, HeldInputs>());
  const requestedInputsRef = useRef<Record<InputControl, boolean>>({ ...EMPTY_INPUT });
  const receivedRef = useRef(new Map<string, number>());
  const originTickRef = useRef(0);
  const lastSnapshotTickRef = useRef(0);
  const lastCheckpointTickRef = useRef(0);
  const resultSentRef = useRef(false);
  const qualityRef = useRef({ rtt: 0, jitter: 0, loss: 0, samples: 0 });
  const previousRttRef = useRef(0);
  const resultTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const casualConnectionStartedRef = useRef(false);
  const casualFallbackIdRef = useRef("");
  const casualDeadlineRef = useRef<number | undefined>(undefined);
  const lastLockCueRef = useRef(new Map<string, { lockSeq: number; clearedLines: number }>());
  const liveMatchStatsRef = useRef(new Map<string, LiveMatchStats>());
  const playedLockCueRef = useRef(new Map<string, number>());
  const roomCountDialogPositionRef = useRef(roomCountDialogPosition);
  const roomCountDragRef = useRef<{ pointerId: number; startX: number; startY: number; startTop: number; startRight: number } | null>(null);
  const chatLogRef = useRef<HTMLDivElement | null>(null);
  const { playCue, playLock, unlock } = useLegacyAudio();
  const { settings: audioSettings, updateSettings: updateAudioSettings } = useLegacyAudioSettings();

  useEffect(() => {
    const log = chatLogRef.current;
    if (!log) return;
    log.scrollTop = log.scrollHeight;
  }, [chat.length]);

  const send = useCallback((message: GameRoomClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }, []);

  const submitChat = useCallback(() => {
    const text = chatText.trim();
    if (!text) return;
    send({ type: "chat", text, color: chatColor });
    setChatText("");
    void unlock().then(() => playCue("message"));
  }, [chatColor, chatText, playCue, send, unlock]);

  const setScreenPhase = useCallback((next: ScreenPhase) => {
    screenRef.current = next;
    setScreen(next);
  }, []);

  const applyRoom = useCallback((nextRoom: RoomView) => {
    roomRef.current = nextRoom;
    matchRef.current = nextRoom.match;
    setRoom(nextRoom);
    setActiveMatch(nextRoom.match);
  }, []);

  const sendHostResult = useCallback((reason: string) => {
    const match = matchRef.current;
    if (!match || resultSentRef.current) return;
    resultSentRef.current = true;
    const winners = match.players
      .map((player) => player.id)
      .filter((id) => !gamesRef.current.get(id)?.topOut);
    send({ type: "result", matchId: match.id, winners, reason });
  }, [send]);

  const sendHostSnapshot = useCallback((tick: number) => {
    const match = matchRef.current;
    if (!match || match.matchHostId !== memberIdRef.current) return;
    const states: TetrisView[] = [];
    const checkpoints: Record<string, JsonValue> = {};
    for (const player of match.players) {
      const game = gamesRef.current.get(player.id);
      if (!game) continue;
      const current = viewFromGame(game, receivedRef.current.get(player.id) ?? 0);
      const previousStats = liveMatchStatsRef.current.get(player.id) ?? { lines: 0, pieces: 0 };
      const liveStats = {
        lines: previousStats.lines,
        pieces: game.blockIndex,
      };
      liveMatchStatsRef.current.set(player.id, liveStats);
      states.push({
        playerId: player.id,
        tick: game.tick,
        board: current.board,
        active: { ...game.active },
        nextPiece: game.nextPiece,
        pending: current.pending,
        received: current.received,
        lines: liveStats.lines,
        pieces: liveStats.pieces,
        topOut: game.topOut,
        lastLockSeq: lastLockCueRef.current.get(player.id)?.lockSeq ?? 0,
        lastClearedLines: lastLockCueRef.current.get(player.id)?.clearedLines ?? 0,
      });
      if (tick - lastCheckpointTickRef.current >= 240) {
        checkpoints[player.id] = game.snapshot() as unknown as JsonValue;
      }
    }
    if (tick - lastCheckpointTickRef.current >= 240) lastCheckpointTickRef.current = tick;
    setViews(Object.fromEntries(states.map((state) => [state.playerId, viewFromWire(state)])));
    send({
      type: "snapshot",
      matchId: match.id,
      tick,
      states,
      ...(Object.keys(checkpoints).length ? { checkpoints } : {}),
    });
    lastSnapshotTickRef.current = tick;
  }, [send]);

  const handleServerMessage = useCallback((message: GameRoomServerMessage) => {
    switch (message.type) {
      case "welcome": {
        memberIdRef.current = message.memberId;
        setMemberId(message.memberId);
        writeRoomRejoinToken(message.room.roomCode, message.rejoinToken);
        applyRoom(message.room);
        setServerRecord(normalizeRoomRecord(message.stats));
        setConnected(true);
        const welcomePhase = message.room.match && message.room.match.phase !== "result" ? message.room.match.phase : "waiting";
        setScreenPhase(welcomePhase);
        setStatus(message.resumed ? "ルームへ再入室しました。席を復元しています" : welcomePhase === "waiting" ? "ルームで対戦相手を待っています" : "対戦を観戦中");
        break;
      }
      case "stats":
        setServerRecord(normalizeRoomRecord(message.stats));
        break;
      case "room":
        {
          const previousMemberIds = new Set(roomRef.current?.members.map((member) => member.id) ?? []);
          const someoneJoined = roomRef.current !== null && message.room.members.some((member) => !previousMemberIds.has(member.id));
          applyRoom(message.room);
          if (someoneJoined) void unlock().then(() => playCue("login"));
        }
        if (!message.room.match) setScreenPhase("waiting");
        break;
      case "chat":
        setChat((current) => [...current, message.message].slice(-100));
        break;
      case "match-start": {
        matchRef.current = message.match;
        setActiveMatch(message.match);
        resultSentRef.current = false;
        inputSeqRef.current = 0;
        lastSnapshotTickRef.current = 0;
        lastCheckpointTickRef.current = 0;
        gamesRef.current.clear();
        inputStatesRef.current.clear();
        receivedRef.current.clear();
        lastLockCueRef.current.clear();
        liveMatchStatsRef.current.clear();
        playedLockCueRef.current.clear();
        for (const player of message.match.players) {
          inputStatesRef.current.set(player.id, { ...EMPTY_INPUT });
          receivedRef.current.set(player.id, 0);
          liveMatchStatsRef.current.set(player.id, { lines: 0, pieces: 0 });
          gamesRef.current.set(
            player.id,
            new LegacyGame({ matchId: message.match.id, seed: message.match.seed }),
          );
        }
        originTickRef.current = performance.now();
        setResult(null);
        setViews({});
        setScreenPhase("playing");
        setStatus(message.match.matchHostId === memberIdRef.current ? "対戦ホストとして試合を進行中" : "対戦中");
        break;
      }
      case "input": {
        const match = matchRef.current;
        if (!match || match.matchHostId !== memberIdRef.current || message.matchId !== match.id) break;
        const game = gamesRef.current.get(message.senderId);
        if (!game) break;
        inputStatesRef.current.set(message.senderId, { ...message.input });
        game.setInputs(message.input, message.inputSeq);
        break;
      }
      case "match-state":
        if (matchRef.current?.id !== message.matchId) break;
        for (const state of message.states) {
          liveMatchStatsRef.current.set(state.playerId, {
            lines: state.lines,
            pieces: state.pieces ?? 0,
          });
          const lockSeq = state.lastLockSeq ?? 0;
          const playedLockSeq = playedLockCueRef.current.get(state.playerId) ?? 0;
          if (lockSeq > playedLockSeq) {
            playedLockCueRef.current.set(state.playerId, lockSeq);
            playLock(state.lastClearedLines ?? 0);
          }
        }
        setViews(Object.fromEntries(message.states.map((state) => [state.playerId, viewFromWire(state)])));
        if (screenRef.current === "paused") setScreenPhase("playing");
        break;
      case "match-paused":
        if (matchRef.current?.id !== message.matchId) break;
        if (matchRef.current) {
          const nextMatch = { ...matchRef.current, phase: "paused" as const, matchHostId: message.matchHostId ?? matchRef.current.matchHostId };
          matchRef.current = nextMatch;
          setActiveMatch(nextMatch);
        }
        setScreenPhase("paused");
        setStatus(message.reason);
        break;
      case "match-migrated": {
        const match = matchRef.current;
        if (!match || match.id !== message.matchId) break;
        const nextMatch = { ...match, phase: "paused" as const, matchHostId: message.matchHostId };
        matchRef.current = nextMatch;
        setActiveMatch(nextMatch);
        if (message.matchHostId !== memberIdRef.current) {
          setScreenPhase("paused");
          setStatus("対戦ホストの引き継ぎを待っています…");
          break;
        }
        gamesRef.current.clear();
        for (const player of match.players) {
          const checkpoint = message.checkpoints[player.id];
          try {
            gamesRef.current.set(
              player.id,
              validCheckpoint(checkpoint)
                ? LegacyGame.fromSnapshot(checkpoint as unknown as GameSnapshot)
                : new LegacyGame({ matchId: match.id, seed: match.seed }),
            );
          } catch {
            gamesRef.current.set(player.id, new LegacyGame({ matchId: match.id, seed: match.seed }));
          }
        }
        originTickRef.current = performance.now() - message.tick / 0.12;
        lastSnapshotTickRef.current = message.tick;
        lastCheckpointTickRef.current = message.tick;
        resultSentRef.current = false;
        setScreenPhase("playing");
        setStatus("対戦ホストを引き継ぎました");
        break;
      }
      case "match-result":
        if (matchRef.current?.id !== message.matchId) break;
        if (
          matchRef.current.players.some((player) => player.id === memberIdRef.current) &&
          message.winners.length > 0 &&
          !message.winners.includes(memberIdRef.current)
        ) {
          recordLossAndMaybeShowInterstitial();
        }
        if (matchRef.current) {
          const nextMatch = { ...matchRef.current, phase: "result" as const };
          matchRef.current = nextMatch;
          setActiveMatch(nextMatch);
        }
        setResult({ winners: message.winners, reason: message.reason });
        setScreenPhase("result");
        setStatus("試合終了");
        if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
        if (!casualMode) {
          resultTimerRef.current = setTimeout(() => {
            setScreenPhase("waiting");
            setStatus("部屋に戻りました。次の一戦を待っています");
          }, 1_700);
        }
        break;
      case "pong": {
        const rtt = Math.max(0, performance.now() - message.sentAt);
        const previous = previousRttRef.current || rtt;
        previousRttRef.current = rtt;
        qualityRef.current = {
          rtt: qualityRef.current.samples ? qualityRef.current.rtt * 0.8 + rtt * 0.2 : rtt,
          jitter: qualityRef.current.samples ? qualityRef.current.jitter * 0.8 + Math.abs(rtt - previous) * 0.2 : 0,
          loss: 0,
          samples: Math.min(100, qualityRef.current.samples + 1),
        };
        send({ type: "quality", quality: qualityRef.current });
        break;
      }
      case "error":
        setError(true);
        setStatus(message.message);
        break;
    }
  }, [applyRoom, casualMode, playCue, playLock, send, setScreenPhase, unlock]);

  const connect = useCallback(() => {
    const selectedRoom = casualMode
      ? CASUAL_ROOM_CODE
      : customMode || MULTI_ROOM_ENTRY_OPTIONS.some((option) => option.value === roomCode)
      ? roomCode
      : MULTI_ROOM_OPTIONS[0].value;
    const normalizedRoom = selectedRoom.trim().toUpperCase().replace(/[^A-Z0-9]/gu, "");
    const profile = splitProfile(normalizePlayerProfile(playerName));
    if (casualMode && !casualFallbackIdRef.current) {
      casualFallbackIdRef.current = typeof crypto.randomUUID === "function"
        ? `casual-${crypto.randomUUID()}`
        : `casual-${Math.random().toString(36).slice(2)}`;
    }
    const normalizedName = (profile.name || (casualMode ? "PLAYER" : "")).slice(0, ROOM_NAME_MAX_LENGTH);
    const normalizedTripKey = profile.trip.slice(0, ROOM_TRIP_MAX_LENGTH);
    const normalizedTrip = create2chTrip(normalizedTripKey);
    const normalizedPlayerId = (playerId.trim() || (casualMode ? casualFallbackIdRef.current : "")).slice(0, 64);
    if (normalizedRoom.length < 6 || normalizedRoom.length > 12 || !normalizedPlayerId || !normalizedName) {
      setError(true);
      setStatus("IDと名前を入力してください。トリップキーは名前の後ろに # で入力できます");
      return;
    }
    void unlock();
    try {
      const socket = new WebSocket(roomWebSocketUrl(normalizedRoom));
      const rejoinToken = readRoomRejoinToken(normalizedRoom);
      socketRef.current = socket;
      setRoomCode(normalizedRoom);
      setError(false);
      setStatus("ルームへ接続しています…");
      socket.addEventListener("open", () => {
        if (socketRef.current !== socket) return;
        socket.send(JSON.stringify({
          type: "join",
          profileId: normalizedPlayerId,
          name: normalizedName,
          ...(normalizedTrip ? { trip: normalizedTrip } : {}),
          ...(rejoinToken ? { rejoinToken } : {}),
          ...(casualMode ? { queue: "casual" as const } : {}),
        } satisfies GameRoomClientMessage));
      });
      socket.addEventListener("message", (event) => {
        if (socketRef.current !== socket) return;
        try {
          const decoded: unknown = JSON.parse(String(event.data));
          if (isServerMessage(decoded)) handleServerMessage(decoded);
        } catch {
          setError(true);
          setStatus("ルームからの応答を読み取れませんでした");
        }
      });
      socket.addEventListener("error", () => {
        if (socketRef.current !== socket) return;
        setError(true);
        setStatus("ルームへ接続できませんでした");
      });
      socket.addEventListener("close", () => {
        if (socketRef.current !== socket) return;
        socketRef.current = null;
        setConnected(false);
        if (screenRef.current !== "entry") {
          setScreenPhase("entry");
          setStatus("接続が終了しました");
        }
      });
      // Keep the key in the local profile field so reconnects can derive the
      // same trip again; only the generated trip is sent over the socket.
      const normalizedProfile = `${normalizedName}${normalizedTripKey ? `#${normalizedTripKey}` : ""}`;
      const profileChatColor = readChatColor(normalizedPlayerId);
      setPlayerName(normalizedProfile);
      setPlayerId(normalizedPlayerId);
      setChatColor(profileChatColor);
      setChatColorDraft(profileChatColor);
      if (!casualMode || playerName.trim()) onPlayerNameChange?.(normalizedProfile);
      if (!casualMode || playerId.trim()) onPlayerIdChange?.(normalizedPlayerId);
    } catch (reason) {
      setError(true);
      setStatus(reason instanceof Error ? reason.message : "ルームURLを作成できませんでした");
    }
  }, [casualMode, customMode, handleServerMessage, onPlayerIdChange, onPlayerNameChange, playerId, playerName, roomCode, setScreenPhase, unlock]);

  useEffect(() => {
    if (!casualMode || casualFallback || casualConnectionStartedRef.current) return;
    casualConnectionStartedRef.current = true;
    connect();
  }, [casualFallback, casualMode, connect]);

  const closeRoom = useCallback(() => {
    if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify({ type: "leave" } satisfies GameRoomClientMessage));
      } catch {
        // The close path still clears the local room state if the socket is racing to close.
      }
    }
    socket?.close(1000, "leave");
    socketRef.current = null;
    removeRoomRejoinToken(roomCode.trim().toUpperCase().replace(/[^A-Z0-9]/gu, ""));
    gamesRef.current.clear();
    setConnected(false);
    setMemberId("");
    memberIdRef.current = "";
    matchRef.current = null;
    roomRef.current = null;
    setRoom(null);
    setActiveMatch(null);
    setViews({});
    lastLockCueRef.current.clear();
    liveMatchStatsRef.current.clear();
    playedLockCueRef.current.clear();
    setChat([]);
    setResult(null);
    setScreenPhase("entry");
    setStatus("IDと名前を入力してルームへ");
  }, [roomCode, setScreenPhase]);

  const openChatColorDialog = useCallback(() => {
    setRoomCountsOpen(false);
    setSelectedMemberId(null);
    setChatColorDraft(chatColor);
    setColorDialogOpen(true);
  }, [chatColor]);

  const updateDraftRgb = useCallback((channel: "r" | "g" | "b", value: string) => {
    const rgb = rgbFromHex(chatColorDraft);
    const numeric = Number.parseInt(value, 10);
    const next = Number.isFinite(numeric) ? Math.min(255, Math.max(0, numeric)) : 0;
    setChatColorDraft(hexFromRgb(channel === "r" ? next : rgb.r, channel === "g" ? next : rgb.g, channel === "b" ? next : rgb.b));
  }, [chatColorDraft]);

  const applyChatColor = useCallback(() => {
    const next = normalizeChatColor(chatColorDraft);
    setChatColor(next);
    setChatColorDraft(next);
    try {
      const key = chatColorStorageKey(playerId);
      if (key) window.localStorage.setItem(key, next);
    } catch {
      // The color remains active for this room even when storage is unavailable.
    }
    setColorDialogOpen(false);
  }, [chatColorDraft, playerId]);

  const refreshRoomCounts = useCallback(async () => {
    setRoomCountsLoading(true);
    setRoomCountsError("");
    try {
      const origin = originForRoom();
      const responses = await Promise.all(MULTI_ROOM_OPTIONS.map(async (option) => {
        const currentRoom = roomRef.current;
        if (currentRoom?.roomCode === option.value) {
          const connectedMembers = currentRoom.members.filter((member) => member.connected).length;
          return [option.value, { connectedCount: connectedMembers, totalCount: currentRoom.members.length }] as const;
        }
        try {
          const response = await fetch(`${origin}/v2/rooms/${encodeURIComponent(option.value)}/status`, { cache: "no-store" });
          if (!response.ok) return [option.value, null] as const;
          const value: unknown = await response.json();
          if (typeof value !== "object" || value === null) return [option.value, null] as const;
          const data = value as { connectedCount?: unknown; totalCount?: unknown };
          if (!Number.isSafeInteger(data.connectedCount) || !Number.isSafeInteger(data.totalCount)) return [option.value, null] as const;
          return [option.value, {
            connectedCount: Math.max(0, Number(data.connectedCount)),
            totalCount: Math.max(0, Number(data.totalCount)),
          }] as const;
        } catch {
          return [option.value, null] as const;
        }
      }));
      const next = Object.fromEntries(responses) as Record<string, RoomCount | null>;
      setRoomCounts(next);
      if (responses.every(([, value]) => value === null)) setRoomCountsError("人数を取得できませんでした。しばらくしてから再試行してください。");
    } catch {
      setRoomCountsError("人数確認サーバーに接続できませんでした。");
    } finally {
      setRoomCountsLoading(false);
    }
  }, []);

  const openRoomCounts = useCallback(() => {
    setColorDialogOpen(false);
    setSelectedMemberId(null);
    setRoomCountsOpen(true);
    void refreshRoomCounts();
  }, [refreshRoomCounts]);

  const saveRoomCountDialogPosition = useCallback((position: RoomCountDialogPosition) => {
    try {
      window.localStorage.setItem(ROOM_COUNT_DIALOG_POSITION_KEY, JSON.stringify(position));
    } catch {
      // The current position remains active for this room even when storage is unavailable.
    }
  }, []);

  const updateRoomCountDialogPosition = useCallback((position: RoomCountDialogPosition) => {
    roomCountDialogPositionRef.current = position;
    setRoomCountDialogPosition(position);
  }, []);

  const closeRoomCounts = useCallback(() => {
    saveRoomCountDialogPosition(roomCountDialogPositionRef.current);
    setRoomCountsOpen(false);
  }, [saveRoomCountDialogPosition]);

  const openMemberRecord = useCallback((id: string) => {
    setColorDialogOpen(false);
    setRoomCountsOpen(false);
    setSelectedMemberId((current) => current === id ? null : id);
  }, []);

  const closeMemberRecord = useCallback(() => {
    saveRoomCountDialogPosition(roomCountDialogPositionRef.current);
    setSelectedMemberId(null);
  }, [saveRoomCountDialogPosition]);

  const handleRoomCountPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.pointerType !== "touch") return;
    if (event.target instanceof Element && event.target.closest("button")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    roomCountDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTop: roomCountDialogPositionRef.current.top,
      startRight: roomCountDialogPositionRef.current.right,
    };
  }, []);

  const handleRoomCountPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = roomCountDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    updateRoomCountDialogPosition({
      top: drag.startTop + Math.round(event.clientY - drag.startY),
      right: drag.startRight - Math.round(event.clientX - drag.startX),
    });
  }, [updateRoomCountDialogPosition]);

  const handleRoomCountPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = roomCountDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    roomCountDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    saveRoomCountDialogPosition(roomCountDialogPositionRef.current);
  }, [saveRoomCountDialogPosition]);

  const sendRecordToChat = useCallback(() => {
    send({ type: "record" });
    void unlock().then(() => playCue("message"));
  }, [playCue, send, unlock]);

  const startRoomCpu = useCallback((humanSeat: 1 | 2) => {
    const nextId = `cpu-room:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    setRoomCpuHumanSeat(humanSeat);
    setRoomCpuOpponentName(randomRoomCpuName());
    setRoomCpuMatchId(nextId);
    setStatus("ルーム接続を維持したままCPU戦を開始します");
  }, []);

  const handleRoomCpuResult = useCallback((cpuResult: "win" | "loss" | "draw") => {
    if (!roomCpuMatchId) return;
    send({ type: "cpu-result", matchId: roomCpuMatchId, result: cpuResult });
  }, [roomCpuMatchId, send]);

  const leaveRoomCpu = useCallback(() => {
    setRoomCpuMatchId(null);
    setStatus("CPU戦が終了しました。ルームで次の対戦を待っています");
  }, []);

  useEffect(() => {
    if (!casualMode || casualFallback) return;
    casualDeadlineRef.current ??= Date.now() + CASUAL_WAIT_SECONDS * 1_000;
    const updateRemaining = () => {
      const remaining = Math.max(0, Math.ceil(((casualDeadlineRef.current ?? Date.now()) - Date.now()) / 1_000));
      setCasualRemaining(remaining);
      if (remaining > 0 || ["playing", "paused", "result"].includes(screenRef.current)) return;
      closeRoom();
      setStatus("対戦相手が見つからないため、CPU戦を開始します");
      setCasualFallback(randomCasualCpuName());
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 250);
    return () => window.clearInterval(timer);
  }, [casualFallback, casualMode, closeRoom]);

  useEffect(() => {
    if (!casualMode || screen !== "result") return;
    const timer = window.setTimeout(() => {
      closeRoom();
      onBack();
    }, 1_700);
    return () => window.clearTimeout(timer);
  }, [casualMode, closeRoom, onBack, screen]);

  const handleInput = useCallback((action: LegacyAction, held: boolean) => {
    void unlock();
    const match = matchRef.current;
    if (!match || screenRef.current !== "playing") return;
    const member = roomRef.current?.members.find((candidate) => candidate.id === memberIdRef.current);
    if (!member?.seat && member?.seat !== 0) return;
    const control = ACTION_TO_INPUT[action];
    if (requestedInputsRef.current[control] === held) return;
    requestedInputsRef.current[control] = held;
    const input = { ...(inputStatesRef.current.get(memberIdRef.current) ?? EMPTY_INPUT), [control]: held };
    inputStatesRef.current.set(memberIdRef.current, input);
    const inputSeq = ++inputSeqRef.current;
    if (match.matchHostId === memberIdRef.current) {
      gamesRef.current.get(memberIdRef.current)?.setInputs(input, inputSeq);
    } else {
      send({ type: "input", matchId: match.id, input, inputSeq });
    }
  }, [send, unlock]);

  useLegacyKeyboard(screen === "playing", handleInput, "A", keyBindings);

  useEffect(() => {
    const resetRootScroll = () => {
      window.scrollTo(0, 0);
      window.requestAnimationFrame(() => window.scrollTo(0, 0));
    };
    resetRootScroll();
    const settle = window.setTimeout(resetRootScroll, 120);
    return () => window.clearTimeout(settle);
  }, [screen]);

  useEffect(() => {
    let frame = 0;
    const loop = () => {
      const match = matchRef.current;
      if (screenRef.current === "playing" && match && match.matchHostId === memberIdRef.current && gamesRef.current.size) {
        const targetTick = Math.floor((performance.now() - originTickRef.current) * 0.12);
        for (const [playerId, game] of gamesRef.current) {
          if (targetTick <= game.tick) continue;
          const events = game.stepTo(targetTick);
          for (const event of events) {
            if (event.type === "lock") {
              lastLockCueRef.current.set(playerId, {
                lockSeq: event.lockSeq,
                clearedLines: event.clearedLines,
              });
              const previousStats = liveMatchStatsRef.current.get(playerId) ?? { lines: 0, pieces: 0 };
              liveMatchStatsRef.current.set(playerId, {
                lines: previousStats.lines + event.clearedLines,
                pieces: previousStats.pieces + 1,
              });
              const received = receivedRef.current.get(playerId) ?? 0;
              receivedRef.current.set(playerId, received + event.garbageApplied);
              if (event.attackLines > 0) {
                for (const other of match.players) {
                  if (other.id === playerId) continue;
                  gamesRef.current.get(other.id)?.applyScheduledAttack({
                    attackId: `${playerId}:${event.lockSeq}`,
                    createdTick: event.tick,
                    eligibleTick: event.tick + DEFAULT_ATTACK_DELAY_TICKS,
                    lines: event.attackLines,
                    sourceLockSeq: event.lockSeq,
                  });
                }
              }
            }
          }
        }
        if (gamesRef.current.size && [...gamesRef.current.values()].some((game) => game.topOut)) sendHostResult("TOP OUT");
        const currentTick = Math.max(...[...gamesRef.current.values()].map((game) => game.tick));
        if (currentTick >= lastSnapshotTickRef.current + 6) sendHostSnapshot(currentTick);
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [sendHostResult, sendHostSnapshot]);

  useEffect(() => {
    if (!connected) return;
    const timer = window.setInterval(() => send({ type: "ping", sentAt: performance.now() }), 1_500);
    return () => window.clearInterval(timer);
  }, [connected, send]);

  useEffect(() => () => {
    if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
    socketRef.current?.close(1000, "unmount");
  }, []);

  const currentMember: RoomMember | undefined = room?.members.find((member) => member.id === memberId);
  const match = room?.match ?? activeMatch;
  const localSeat = currentMember?.seat ?? null;
  const playerForSeat = (seat: 0 | 1) => match?.players.find((player) => player.seat === seat)?.id;
  const p1Id = playerForSeat(0) ?? room?.members.find((member) => member.seat === 0)?.id;
  const p2Id = playerForSeat(1) ?? room?.members.find((member) => member.seat === 1)?.id;
  const p1 = (p1Id && views[p1Id]) || emptyView();
  const p2 = (p2Id && views[p2Id]) || emptyView();
  const p1Member = room?.members.find((member) => member.id === p1Id);
  const p2Member = room?.members.find((member) => member.id === p2Id);
  const p1Name = p1Member?.name ?? "PLAYER 1";
  const p2Name = p2Member?.name ?? "PLAYER 2";
  const rankForMember = (id: string | undefined): RankTitle => {
    if (!id) return "5級";
    const memberRank = room?.members.find((member) => member.id === id)?.rank;
    return memberRank ?? (id === memberId ? serverRecord.rank : "5級");
  };
  const p1Rank = rankForMember(p1Id);
  const p2Rank = rankForMember(p2Id);
  const p1Status = matchStatusForMember(p1Member, p1Id === memberId ? serverRecord : undefined);
  const p2Status = matchStatusForMember(p2Member, p2Id === memberId ? serverRecord : undefined);
  const p1BattleStatus = liveMatchStatusForView(0, p1);
  const p2BattleStatus = liveMatchStatusForView(1, p2);
  const quality = currentMember ? `ROOM HOST ${room?.roomHostId === memberId ? "· YOU" : ""} / MATCH HOST ${room?.matchHostId === memberId ? "· YOU" : ""} / ${qualityLabel(currentMember.quality)}` : "ROOM";
  const selectedMember = room?.members.find((member) => member.id === selectedMemberId);
  const selectedMemberRecord = selectedMember?.id === memberId ? serverRecord : selectedMember?.record;
  const draftRgb = rgbFromHex(chatColorDraft);
  const seatButton = (seat: 0 | 1) => {
    const occupant = room?.members.find((member) => member.seat === seat);
    const ownMember = room?.members.find((member) => member.id === memberId);
    const ownSeat = ownMember?.seat ?? null;
    const spectatorOnly = ownMember?.spectatorOnly === true;
    const chooseSeat = () => {
      if (spectatorOnly) return;
      if (occupant?.id === memberId) {
        send({ type: "seat", seat: null });
        return;
      }
      if (!occupant && cpuEnabled && ownSeat !== null && ownSeat !== seat && (!match || match.phase === "result")) {
        startRoomCpu((ownSeat + 1) as 1 | 2);
        return;
      }
      send({ type: "seat", seat: occupant?.id === memberId ? null : seat });
    };
    return (
      <button
        aria-label={`P${seat + 1}${occupant ? ` ${formatPlayerIdentity(occupant.name, occupant.trip)}` : " エントリー"}`}
        className={`room-seat-button${occupant?.id === memberId ? " selected" : ""}`}
        disabled={spectatorOnly || Boolean(occupant && occupant.id !== memberId) || Boolean(match && match.phase !== "result")}
        key={seat}
        onClick={chooseSeat}
        type="button"
      >
        <strong>P{seat + 1}</strong>
        <small>{occupant ? formatPlayerIdentity(occupant.name, occupant.trip) : "＋ エントリー"}</small>
      </button>
    );
  };
  const playerInfoStrip = (seat: 0 | 1, member: RoomMember | undefined, status: MatchStatus) => {
    const entered = Boolean(member && member.seat === seat);
    const playerLabel = member ? formatPlayerIdentity(member.name, member.trip) : `P${seat + 1}`;
    return (
      <div
        aria-label={entered ? `${playerLabel}の戦績 ${status.text}` : `P${seat + 1} エントリー待ち`}
        className={`room-original-player-strip${entered ? " is-entered" : ""}`}
      >
        {entered ? (
          <>
            <strong>{playerLabel}</strong>
            <span>{status.text}</span>
          </>
        ) : (
          <>
            <strong>P{seat + 1}</strong>
            <span>エントリー待ち</span>
          </>
        )}
      </div>
    );
  };

  // Keep all room hooks and the WebSocket alive while the settings panel is open.
  if (settingsOpen) return null;

  if (casualFallback) {
    return (
      <CpuMode
        keyBindings={keyBindings}
        onBack={onBack}
        opponentName={casualFallback}
        returnHomeAfterResult
      />
    );
  }

  if (roomCpuMatchId) {
    return (
      <CpuMode
        keyBindings={keyBindings}
        onBack={leaveRoomCpu}
        onResult={handleRoomCpuResult}
        opponentName={roomCpuOpponentName}
        humanSeat={roomCpuHumanSeat}
        returnToRoomAfterResult
      />
    );
  }

  if (casualMode && (screen === "entry" || screen === "waiting")) {
    return (
      <main className="mode-shell matchmaking-shell casual-queue-shell">
        <header className="mode-header">
          <button className="back-button" onClick={() => { closeRoom(); onBack(); }} type="button">← メニュー</button>
          <span className="mode-header-label">CASUAL MATCH / BLOCK BATTLE</span>
        </header>
        <section aria-labelledby="casual-queue-title" className="matchmaking-card casual-queue-card">
          <span aria-hidden="true" className="matchmaking-emblem">✦</span>
          <span className="mode-badge">NO RECORD / 1 GAME</span>
          <h1 id="casual-queue-title">対戦相手を探しています</h1>
          <p className="matchmaking-lead">CASUAL MATCH</p>
          <div aria-label={`残り${casualRemaining}秒`} className="casual-queue-timer">
            <strong>{casualRemaining}</strong>
            <span>SEC</span>
          </div>
          <p className="matchmaking-detail">相手が見つかると、そのまま1試合を開始します。試合終了後はホームへ戻ります。</p>
          <p className="casual-queue-status" role="status">{connected ? "対戦相手の接続を待っています…" : status}</p>
          <button className="quiet-room-button" onClick={() => { closeRoom(); onBack(); }} type="button">マッチングをキャンセル</button>
        </section>
        <AdSlot format="horizontal" label="カジュアルマッチの広告スペース" />
      </main>
    );
  }

  if (screen === "entry") {
    return (
      <main className="lobby-shell room-entry-shell">
        <header className="mode-header lobby-header">
          <button className="back-button" onClick={onBack} type="button">← メニュー</button>
          <span className="mode-header-label">{customMode ? "CUSTOM ROOM / BLOCK BATTLE" : "MULTI ROOMS / BLOCK BATTLE"}</span>
        </header>
        <section className="lobby-panel">
          <p className="brand-kicker">{customMode ? "CUSTOM ROOM / MATCH HOST" : "ROOM HOST / MATCH HOST SEPARATED"}</p>
          <h1 className="brand-title">BLOCK WAKUWAKU<br />FRONTIER</h1>
          <p className="brand-subtitle">{customMode ? "CUSTOM ROOM BLOCK BATTLE" : "MULTI ROOM BLOCK BATTLE"}</p>
          <form className="room-form" onSubmit={(event) => { event.preventDefault(); connect(); }}>
            {!customMode ? (
              <>
                <label htmlFor="multi-room-select">ROOM</label>
                <select
                  className="room-code room-select"
                  id="multi-room-select"
                  onChange={(event) => setRoomCode(event.target.value)}
                  value={MULTI_ROOM_ENTRY_OPTIONS.some((option) => option.value === roomCode) ? roomCode : MULTI_ROOM_OPTIONS[0].value}
                >
                  {MULTI_ROOM_ENTRY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </>
            ) : null}
            <label htmlFor="room-player-name">NAME <small>名前10文字まで #トリップキー（任意）</small></label>
            <input className="room-code room-profile-input" id="room-player-name" maxLength={ROOM_PROFILE_MAX_LENGTH} onChange={(event) => { const nextProfile = normalizePlayerProfile(event.target.value); setPlayerName(nextProfile); onPlayerNameChange?.(nextProfile); }} placeholder="名前#トリップキー" value={playerName} />
            <label htmlFor="room-player-id">ID <small>戦績保存用</small></label>
            <input className="room-code room-profile-input" id="room-player-id" maxLength={64} onChange={(event) => { setPlayerId(event.target.value); onPlayerIdChange?.(event.target.value); }} placeholder="自分で決めたID" value={playerId} />
            {customMode ? (
              <>
                <label htmlFor="game-room-code">ROOM ID <small>6〜12文字</small></label>
                <input className="room-code" id="game-room-code" maxLength={12} minLength={6} onChange={(event) => setRoomCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/gu, ""))} placeholder="好きな部屋ID" value={roomCode} />
              </>
            ) : null}
            <button className="chrome-button" type="submit">ルームに入る</button>
          </form>
          {!customMode ? <p className="room-entry-hint">ROOM1〜6・精神と時の部屋から選択して入室します。</p> : <p className="room-entry-hint">同じROOM IDを入力した人同士で対戦します。</p>}
          <p className={`lobby-status${error ? " error" : ""}`} role="status">{status}</p>
        </section>
        <AdSlot format="horizontal" label="マルチルームの広告スペース" />
      </main>
    );
  }

  if (screen === "playing" || screen === "paused" || screen === "result") {
    return (
      <main className="room-mode-shell">
        <div className="room-mode-ribbon">ROOM {room?.roomCode ?? roomCode} / {screen === "paused" ? "PAUSED" : screen === "result" ? "FINISH" : "BLOCK BATTLE"} / {status}</div>
        <BattleArena
          controlsEnabled={screen === "playing" && localSeat !== null}
          localSeat={localSeat === null ? 0 : (localSeat + 1) as 1 | 2}
          onInput={handleInput}
          p1={p1}
          p2={p2}
          p1Label={`${p1Name} / ${p1Rank}`}
          p2Label={`${p2Name} / ${p2Rank}`}
          p1Status={p1BattleStatus}
          p2Status={p2BattleStatus}
          quality={quality}
        />
        {result ? (
          <div className="room-result-panel">
            <section className="result-card">
              <h2>{result.winners.includes(memberId) ? "YOU WIN" : result.winners.length ? "YOU LOSE" : "DRAW"}</h2>
              <p>{result.reason}</p>
              {casualMode ? <p>ホームへ戻ります…</p> : <button className="chrome-button" onClick={() => setScreenPhase("waiting")} type="button">部屋に戻る</button>}
            </section>
            <AdSlot format="horizontal" label="試合結果の広告スペース" />
          </div>
        ) : null}
      </main>
    );
  }

  const roomPanelLabel = customMode
    ? (room?.roomCode ?? roomCode)
    : MULTI_ROOM_ENTRY_OPTIONS.find((option) => option.value === (room?.roomCode ?? roomCode))?.label ?? (casualMode ? "CASUAL" : "ROOM1");

  return (
    <main className="room-mode-shell room-waiting-shell">
      <header className="room-mode-header">
        <div>
          <span className="mode-header-label">{customMode ? "CUSTOM ROOM / BLOCK BATTLE" : "MULTI ROOM / BLOCK BATTLE"}</span>
          <h1>{room?.roomCode ?? roomCode}</h1>
        </div>
        <div className="room-mode-actions">
          <span className="connection-status">{connected ? "ONLINE" : "OFFLINE"}</span>
          <button className="quiet-room-button" onClick={() => { closeRoom(); onBack(); }} type="button">退出</button>
        </div>
      </header>
      <div className="room-original-layout">
        <section aria-label="対戦待機プレビュー" className="room-original-stage">
          <BattleBoard active={p1.active} board={p1.board} label={p1Name} local={localSeat === 0} topOut={p1.topOut} />
          <div className="room-original-center">
            <div className="room-original-next-row">
              <NextPiece cells={p1.nextCells} label="P1 NEXT" piece={p1.nextPiece} />
              <span aria-hidden="true" className="room-original-vs">VS</span>
              <NextPiece cells={p2.nextCells} label="P2 NEXT" piece={p2.nextPiece} />
            </div>
            <div className="room-original-score-row"><strong>{p1.received}</strong><span>{match?.phase === "result" ? "FINISH" : "WAITING"}</span><strong>{p2.received}</strong></div>
            <div className="room-original-seat-row">
              {seatButton(0)}
              {seatButton(1)}
            </div>
            <p className="room-original-stage-status">{currentMember?.spectatorOnly ? "名無しは観戦のみです" : match ? "次の一戦を待っています" : "2人がエントリーすると開始"}</p>
          </div>
          <BattleBoard active={p2.active} board={p2.board} label={p2Name} local={localSeat === 1} topOut={p2.topOut} />
        </section>
        <div aria-label="エントリー済みプレイヤーの戦績" className="room-original-player-row">
          {playerInfoStrip(0, p1Member, p1Status)}
          <span aria-hidden="true" className="room-original-player-row-vs">VS</span>
          {playerInfoStrip(1, p2Member, p2Status)}
        </div>

        <aside className="room-original-sidebar">
          <div className="room-original-room-title"><strong>{roomPanelLabel}</strong><span>{room?.members.length ?? 0}人</span></div>
          <button className="room-original-logout" onClick={() => { closeRoom(); onBack(); }} type="button">ログアウト</button>
          <div className="room-member-list">
            {room?.members.map((member) => (
              <button
                aria-expanded={selectedMemberId === member.id}
                className={`room-member-row${member.connected ? "" : " disconnected"}`}
                key={member.id}
                onClick={() => openMemberRecord(member.id)}
                type="button"
              >
                <span className="room-member-copy"><strong>{formatPlayerIdentity(member.name, member.trip)}</strong></span>
              </button>
            ))}
          </div>
          {selectedMember ? (
            <div aria-label={`${formatPlayerIdentity(selectedMember.name, selectedMember.trip)}の戦績`} className="room-control-dialog room-member-record room-member-record-dialog" role="dialog" style={{ top: `${roomCountDialogPosition.top}px`, right: `${roomCountDialogPosition.right}px` }}>
              <div
                className="room-member-record-head room-control-dialog-head room-count-dialog-drag-handle"
                onPointerCancel={handleRoomCountPointerUp}
                onPointerDown={handleRoomCountPointerDown}
                onPointerMove={handleRoomCountPointerMove}
                onPointerUp={handleRoomCountPointerUp}
              >
                <strong>{formatPlayerIdentity(selectedMember.name, selectedMember.trip)}</strong>
                <button aria-label="戦績を閉じる" className="room-member-record-close" onClick={closeMemberRecord} type="button">×</button>
              </div>
              {selectedMemberRecord ? (
                <dl>
                  <div><dt>段位</dt><dd>{selectedMemberRecord.rank}</dd></div>
                  <div><dt>戦績</dt><dd>{selectedMemberRecord.wins}勝 {selectedMemberRecord.losses}敗 {selectedMemberRecord.draws}分</dd></div>
                  <div><dt>試合</dt><dd>{selectedMemberRecord.games} / EXP {selectedMemberRecord.experience}</dd></div>
                </dl>
              ) : <p>このモードでは戦績を保存しません。</p>}
            </div>
          ) : null}
          <div className="room-seat-grid room-sidebar-seat-grid">
            {seatButton(0)}
            {seatButton(1)}
          </div>
          <div className="room-original-controls" aria-label="ルーム操作">
            <div className="room-original-control-grid">
              <button className="room-original-control-button" onClick={() => onCycleBackground?.()} title={`背景を変更（${backgroundIndex + 1}）`} type="button">背景</button>
              <button className="room-original-control-button" onClick={() => onOpenSettings?.()} type="button">設定</button>
              <button className="room-original-control-button" onClick={openChatColorDialog} type="button">色</button>
              <button className="room-original-control-button" onClick={sendRecordToChat} type="button">戦績</button>
            </div>
            <button className="room-original-control-button room-count-button" onClick={openRoomCounts} type="button">ログイン人数確認</button>
            <div aria-label="音とCPUの設定" className="room-check-panel">
              <label className="room-check-control">
                <input checked={!audioSettings.muted} onChange={(event) => updateAudioSettings({ ...audioSettings, muted: !event.target.checked })} type="checkbox" />
                <span>音</span>
              </label>
              <label className="room-check-control">
                <input checked={cpuEnabled} onChange={(event) => setCpuEnabled(event.target.checked)} type="checkbox" />
                <span>CPU</span>
              </label>
            </div>
          </div>
          {colorDialogOpen ? (
            <div aria-label="チャット色の設定" className="room-control-dialog room-color-dialog" role="dialog" style={{ top: `${roomCountDialogPosition.top}px`, right: `${roomCountDialogPosition.right}px` }}>
              <div
                className="room-control-dialog-head room-count-dialog-drag-handle"
                onPointerCancel={handleRoomCountPointerUp}
                onPointerDown={handleRoomCountPointerDown}
                onPointerMove={handleRoomCountPointerMove}
                onPointerUp={handleRoomCountPointerUp}
              >
                <strong>自分のチャット色</strong>
                <button aria-label="チャット色設定を閉じる" className="room-member-record-close" onClick={() => setColorDialogOpen(false)} type="button">×</button>
              </div>
              <div className="room-color-code-row">
                <span aria-label={`作成中の色 ${normalizeChatColor(chatColorDraft)}`} className="room-color-swatch" role="img" style={{ backgroundColor: normalizeChatColor(chatColorDraft) }} />
                <label className="room-color-picker-row"><span>カラーコード</span><input aria-label="チャット色カラーコード" maxLength={7} onChange={(event) => setChatColorDraft(event.target.value)} type="text" value={chatColorDraft} /></label>
              </div>
              <div className="room-rgb-grid">
                {(["r", "g", "b"] as const).map((channel) => (
                  <label key={channel}>
                    <span>{channel.toUpperCase()} <output>{draftRgb[channel]}</output></span>
                    <input aria-label={`${channel.toUpperCase()}値`} className={`room-rgb-slider room-rgb-slider-${channel}`} max="255" min="0" onChange={(event) => updateDraftRgb(channel, event.target.value)} type="range" value={draftRgb[channel]} />
                  </label>
                ))}
              </div>
              <div className="room-control-dialog-actions"><button className="room-original-control-button" onClick={() => setColorDialogOpen(false)} type="button">取消</button><button className="room-original-control-button is-green" onClick={applyChatColor} type="button">決定</button></div>
            </div>
          ) : null}
          {roomCountsOpen ? (
            <div aria-label="ROOM1からROOM6のログイン人数" className="room-control-dialog room-count-dialog" role="dialog" style={{ top: `${roomCountDialogPosition.top}px`, right: `${roomCountDialogPosition.right}px` }}>
              <div
                className="room-control-dialog-head room-count-dialog-drag-handle"
                onPointerCancel={handleRoomCountPointerUp}
                onPointerDown={handleRoomCountPointerDown}
                onPointerMove={handleRoomCountPointerMove}
                onPointerUp={handleRoomCountPointerUp}
              >
                <strong>ログイン人数</strong>
                <button aria-label="ログイン人数を閉じる" className="room-member-record-close" onClick={closeRoomCounts} type="button">×</button>
              </div>
              <div aria-busy={roomCountsLoading} className="room-count-list">
                {MULTI_ROOM_OPTIONS.map((option) => {
                  const count = roomCounts[option.value];
                  return <div key={option.value}><span>{option.label}</span><strong>{count?.connectedCount ?? 0}人</strong></div>;
                })}
              </div>
              {roomCountsError ? <p className="room-control-error">{roomCountsError}</p> : null}
              <button className="room-original-control-button is-green" onClick={() => void refreshRoomCounts()} type="button">再読み込み</button>
            </div>
          ) : null}
        </aside>

        <section className="room-original-chat room-chat-card">
          <div className="room-chat-log" ref={chatLogRef} role="log">
            {chat.map((message) => {
              const messageColor = message.system || message.record ? undefined : message.color ?? (message.memberId === memberId ? chatColor : undefined);
              const messageColorStyle = messageColor ? { color: messageColor } : undefined;
              return (
                <p className={message.system ? `system ${message.systemRole ?? "notice"}` : message.record ? "record" : undefined} key={message.id}>
                  <strong style={messageColorStyle}>{formatPlayerIdentity(message.name, message.trip)}</strong>
                  <span style={messageColorStyle}>{message.text}</span>
                </p>
              );
            })}
          </div>
          <form className="room-chat-form" onSubmit={(event) => { event.preventDefault(); submitChat(); }}>
            <input aria-label="ルームチャット" maxLength={300} onChange={(event) => setChatText(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} placeholder="メッセージを入力" value={chatText} />
            <button className="chrome-button" type="submit">送信</button>
          </form>
        </section>
      </div>
      <AdSlot format="horizontal" label="ルーム画面の広告スペース" />
    </main>
  );
}
